import { useMemo, useCallback } from 'react'
import { BaseTable } from 'Features/Table/BaseTable.view'
import { ExtendedGridColDef } from 'Features/Table/BulkUpdateModal'
import { SheduleTableProps, SynchronizedLinesRowType } from './Scheduler.types'
import {
  Box,
  Button,
  Typography,
  Chip,
  Stack,
  Autocomplete,
  TextField,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import {
  GridRenderCellParams,
  GridRenderEditCellParams,
  useGridApiContext,
} from '@mui/x-data-grid-premium'

const dataConfig = {
  required: ['groupName', 'lines'],
  optional: [
    'changeoverTime',
    'productMasterType',
    'active',
    'periods',
    'location',
  ],
}

type SynchronizedLinesTableProps =
  SheduleTableProps<SynchronizedLinesRowType> & {
    onAddGroup?: () => void
    availableLines?: string[]
    availableLocations?: string[]
  }

// Custom cell renderer to display lines as chips
const LinesCell = ({ value }: GridRenderCellParams) => {
  if (!value) return null
  const lines = String(value)
    .split(',')
    .map((l) => l.trim())
    .filter(Boolean)
  return (
    <Stack
      direction="row"
      spacing={0.5}
      flexWrap="wrap"
      useFlexGap
      sx={{ py: 0.5 }}
    >
      {lines.map((line) => (
        <Chip key={line} label={line} size="small" />
      ))}
    </Stack>
  )
}

// Custom edit component for multi-select lines
const LinesEditCell = ({
  id,
  value,
  field,
  availableLines,
}: GridRenderEditCellParams & { availableLines: string[] }) => {
  const apiRef = useGridApiContext()

  // Parse current value into array
  const currentLines = value
    ? String(value)
        .split(',')
        .map((l) => l.trim())
        .filter(Boolean)
    : []

  const handleChange = useCallback(
    (_event: React.SyntheticEvent, newValue: string[]) => {
      // Convert array back to comma-separated string
      const newValueStr = newValue.join(', ')
      apiRef.current.setEditCellValue({ id, field, value: newValueStr })
    },
    [apiRef, id, field]
  )

  return (
    <Autocomplete
      multiple
      value={currentLines}
      onChange={handleChange}
      options={availableLines}
      disableCloseOnSelect
      size="small"
      sx={{ width: '100%', minWidth: 200 }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          placeholder="Select lines..."
          autoFocus
        />
      )}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option}
            label={option}
            size="small"
          />
        ))
      }
    />
  )
}

// Sentinel value meaning "the constraint applies to every location". Must match
// the literal recognised by the Rust preprocess (case-insensitive comparison
// there, but we emit the canonical capitalisation).
const LOCATION_ALL = 'All'

// Display the location column as chips so multi-select reads naturally.
const LocationCell = ({ value }: GridRenderCellParams) => {
  if (!value) return null
  const locations = String(value)
    .split(',')
    .map((l) => l.trim())
    .filter(Boolean)
  return (
    <Stack
      direction="row"
      spacing={0.5}
      flexWrap="wrap"
      useFlexGap
      sx={{ py: 0.5 }}
    >
      {locations.map((loc) => (
        <Chip
          key={loc}
          label={loc}
          size="small"
          color={loc === LOCATION_ALL ? 'primary' : 'default'}
        />
      ))}
    </Stack>
  )
}

// Multi-select editor for the location column. Selecting "All" collapses any
// existing selection; selecting a specific location while "All" is active
// drops "All" — these two states are mutually exclusive.
const LocationEditCell = ({
  id,
  value,
  field,
  availableLocations,
}: GridRenderEditCellParams & { availableLocations: string[] }) => {
  const apiRef = useGridApiContext()

  const currentLocations = value
    ? String(value)
        .split(',')
        .map((l) => l.trim())
        .filter(Boolean)
    : []

  const options = useMemo(
    () => [
      LOCATION_ALL,
      ...availableLocations.filter((l) => l !== LOCATION_ALL),
    ],
    [availableLocations]
  )

  const handleChange = useCallback(
    (_event: React.SyntheticEvent, newValue: string[]) => {
      const hadAll = currentLocations.includes(LOCATION_ALL)
      const hasAll = newValue.includes(LOCATION_ALL)
      let next = newValue
      if (hasAll && !hadAll) {
        next = [LOCATION_ALL]
      } else if (hasAll && hadAll && newValue.length > 1) {
        next = newValue.filter((v) => v !== LOCATION_ALL)
      }
      apiRef.current.setEditCellValue({
        id,
        field,
        value: next.join(', '),
      })
    },
    [apiRef, id, field, currentLocations]
  )

  return (
    <Autocomplete
      multiple
      value={currentLocations}
      onChange={handleChange}
      options={options}
      disableCloseOnSelect
      size="small"
      sx={{ width: '100%', minWidth: 200 }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          placeholder="Select locations..."
          autoFocus
        />
      )}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option}
            label={option}
            size="small"
            color={option === LOCATION_ALL ? 'primary' : 'default'}
          />
        ))
      }
    />
  )
}

export const SchedulerSynchronizedLinesTable = ({
  data,
  isLoading,
  updateTableData,
  uploadScheduleData,
  isUploading,
  isSaving,
  updateRowsStatus,
  predicate,
  onAddGroup,
  availableLines = [],
  availableLocations = [],
  readOnly = false,
}: SynchronizedLinesTableProps) => {
  const handleDataUpdate = (
    changedRows: Record<number, SynchronizedLinesRowType>
  ) => {
    updateTableData(changedRows)
  }

  const columns: ExtendedGridColDef[] = useMemo(
    () => [
      {
        headerName: 'Group Name',
        field: 'groupName',
        type: 'string',
        flex: 1,
        editable: true,
      },
      {
        headerName: 'Resource',
        field: 'lines',
        type: 'string',
        flex: 2,
        editable: true,
        renderCell: (params: GridRenderCellParams) => <LinesCell {...params} />,
        renderEditCell: (params: GridRenderEditCellParams) => (
          <LinesEditCell {...params} availableLines={availableLines} />
        ),
      },
      {
        headerName: 'Location',
        field: 'location',
        type: 'string',
        flex: 1,
        editable: true,
        renderCell: (params: GridRenderCellParams) => (
          <LocationCell {...params} />
        ),
        renderEditCell: (params: GridRenderEditCellParams) => (
          <LocationEditCell
            {...params}
            availableLocations={availableLocations}
          />
        ),
      },
      {
        headerName: 'Changeover Time (hrs)',
        field: 'changeoverTime',
        type: 'number',
        flex: 1,
        editable: true,
      },
      {
        headerName: 'Product Master Type',
        field: 'productMasterType',
        type: 'singleSelect',
        valueOptions: [
          'temperature',
          'Active',
          'Constraint',
          'Location_constraint',
        ],
        flex: 1,
        editable: true,
      },
      {
        headerName: 'Active',
        field: 'active',
        type: 'number',
        flex: 0.7,
        editable: true,
      },
      {
        headerName: 'Periods',
        field: 'periods',
        type: 'string',
        flex: 1.2,
        editable: true,
        description: 'Time windows e.g. (2,12),(18,26)',
      },
    ],
    [availableLines, availableLocations]
  )

  return (
    <Box>
      {/* Header with Add Group button — hidden in read-only source mode */}
      {!readOnly && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAddGroup}
          >
            Add Group
          </Button>
          {availableLines.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
              Click on the Lines cell to select from {availableLines.length}{' '}
              available lines
            </Typography>
          )}
        </Box>
      )}
      <BaseTable
        data={data ?? []}
        columns={columns}
        isLoading={isLoading}
        updateTableData={handleDataUpdate}
        uploadData={uploadScheduleData}
        isUploading={isUploading}
        isSaving={isSaving}
        checkBoxSelection={true}
        showTableToolBar={true}
        readOnly={readOnly}
        downloadFileName="SchedulerSynchronizedLines"
        multiStageUpload={{
          uploadFile: uploadScheduleData,
          isUploading: isUploading,
          columns: columns,
          updateRowsStatus: updateRowsStatus,
          originalData: data,
          predicate: predicate,
          dataConfig: dataConfig,
        }}
        initialState={{
          pagination: { paginationModel: { pageSize: 100 } },
        }}
        sx={{
          maxWidth: 'calc(100vw - 70px)',
          '& .MuiDataGrid-cell--editable': {
            backgroundColor: '#CBD5E1',
          },
          '& .MuiBox-root': {
            padding: '0px !important',
          },
        }}
      />
    </Box>
  )
}
